/**
 * ErrorsTab Component
 * Displays error logs from Application Insights traces table
 */

import { useState, useEffect } from 'react';
import { AlertTriangle, AlertCircle, XCircle, RefreshCw } from 'react-feather';
import { ErrorEntry, ErrorLogFilter, ErrorLogResult } from '../../types/logs';

interface ErrorsTabProps {
  filter: ErrorLogFilter;
}

type SeverityLevel = 'Warning' | 'Error' | 'Critical';

const severityConfig: Record<SeverityLevel, { icon: React.ElementType; color: string; bgColor: string }> = {
  Warning: { icon: AlertTriangle, color: 'text-yellow-600', bgColor: 'bg-yellow-50' },
  Error: { icon: AlertCircle, color: 'text-red-600', bgColor: 'bg-red-50' },
  Critical: { icon: XCircle, color: 'text-red-800', bgColor: 'bg-red-100' }
};

function formatTimestamp(timestamp: Date | string): string {
  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export default function ErrorsTab({ filter }: ErrorsTabProps) {
  const [data, setData] = useState<ErrorLogResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSeverities, setSelectedSeverities] = useState<SeverityLevel[]>([]);

  useEffect(() => {
    fetchErrors();
  }, [filter, selectedSeverities]);

  async function fetchErrors() {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (filter.startDate) params.set('startDate', filter.startDate.toISOString());
      if (filter.endDate) params.set('endDate', filter.endDate.toISOString());
      if (selectedSeverities.length > 0) params.set('severityLevels', selectedSeverities.join(','));
      if (filter.page) params.set('page', String(filter.page));
      if (filter.pageSize) params.set('pageSize', String(filter.pageSize));

      const response = await fetch(`/api/activity-logs/errors?${params}`);
      const result = await response.json();

      if (result.success) {
        setData(result.data);
      } else {
        setError(result.error || 'Failed to fetch errors');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  }

  function toggleSeverity(severity: SeverityLevel) {
    setSelectedSeverities(prev =>
      prev.includes(severity)
        ? prev.filter(s => s !== severity)
        : [...prev, severity]
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="animate-spin text-gray-400" size={24} />
        <span className="ml-2 text-gray-500">Loading errors...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-red-600">
        <AlertCircle className="mx-auto mb-2" size={24} />
        <p>{error}</p>
        <button
          onClick={fetchErrors}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Severity filter buttons */}
      <div className="flex gap-2">
        {(['Warning', 'Error', 'Critical'] as SeverityLevel[]).map(severity => {
          const config = severityConfig[severity];
          const Icon = config.icon;
          const isSelected = selectedSeverities.includes(severity);

          return (
            <button
              key={severity}
              onClick={() => toggleSeverity(severity)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
                isSelected
                  ? `${config.bgColor} ${config.color} ring-2 ring-offset-1 ring-current`
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Icon size={14} />
              {severity}
            </button>
          );
        })}
      </div>

      {/* Error list */}
      {!data?.errors?.length ? (
        <div className="text-center py-12 text-gray-500">
          <AlertTriangle className="mx-auto mb-2" size={32} />
          <p>No errors found in the selected time range</p>
        </div>
      ) : (
        <div className="space-y-2">
          {data.errors.map((entry: ErrorEntry) => {
            const config = severityConfig[entry.severityLevel];
            const Icon = config.icon;

            return (
              <div
                key={entry.traceId}
                className={`p-4 rounded-lg border ${config.bgColor} border-opacity-50`}
              >
                <div className="flex items-start gap-3">
                  <Icon className={config.color} size={20} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-medium ${config.color}`}>
                        {entry.severityLevel}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatTimestamp(entry.timestamp)}
                      </span>
                      <span className="text-xs text-gray-400">
                        {entry.service}
                      </span>
                    </div>
                    <p className="text-sm text-gray-800 break-words">
                      {entry.message}
                    </p>
                    {entry.operationName && (
                      <p className="text-xs text-gray-500 mt-1">
                        Operation: {entry.operationName}
                      </p>
                    )}
                    {entry.conversationId && (
                      <p className="text-xs text-gray-500">
                        Conversation: {entry.conversationId}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination info */}
      {data && data.totalPages > 1 && (
        <div className="text-center text-sm text-gray-500">
          Page {data.currentPage} of {data.totalPages} ({data.totalCount} total errors)
        </div>
      )}
    </div>
  );
}
